import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { EnhancedButtonStatus } from '@components/button/EnhancedButton';
import ToastErrorMessage from '@components/toast/ToastErrorMessage';
import { drawerSections } from './content';
import { useMutation, useQuery } from '@apollo/client';
import { cloneDeep, isEmpty } from 'lodash';
import { DEFAULT_ERROR_TEXT } from '@utils';
import { IAuthoritiesDrawerProps } from '.';
import { getAssignedLinesList, updateBusinessUserAuthorities } from './queries';
import { getError } from '@utils/graph-utils';
import ToastSuccessMessage from '@components/toast/ToastSuccessMessage';
import { toLookups } from './utils';
import DynamicFormDrawer from '@components/dynamic-form/DynamicFormDrawer';
import { IDynamicFormWidgetSection } from '@components/dynamic-form/types';
import { ISelectFormFieldProps } from '@components/dynamic-form/types/inputTypes';
import { normalizeDynamicFormDrawerValues } from '@components/dynamic-form/utils';

const SalesForceBusinessUserAuthoritiesDrawer: React.FC<
  IAuthoritiesDrawerProps
> = ({ open, onSuccess, onClose, authoritiesDetailsInfo, businessUserId }) => {
  const [formDisabled, setFormDisabled] = useState(false);
  const [submitButtonState, setSubmitButtonState] =
    useState<EnhancedButtonStatus>();
  const [inputsForm, setInputsForm] =
    useState<IDynamicFormWidgetSection[]>(drawerSections);

  const { data: assignedLinesList, loading: assignedLinesLoading } = useQuery(
    getAssignedLinesList,
    {
      variables: { selectedBusinessUserID: businessUserId },
      skip: !businessUserId,
    }
  );
  const [authoritiesAction] = useMutation(updateBusinessUserAuthorities);

  const initialize = () => {
    try {
      const updatedSections = cloneDeep(inputsForm);
      const updatedInputs = updatedSections[0].inputs;

      if (assignedLinesList) {
        const lookupLists = toLookups(assignedLinesList);

        (
          updatedInputs.editableLineCharges as ISelectFormFieldProps
        ).selectOptions = lookupLists;
      }

      if (authoritiesDetailsInfo) {
        updatedInputs.editableLineCharges.value =
          authoritiesDetailsInfo.editableLineCharges;
        updatedInputs.allowPlanCoverEdits.value =
          authoritiesDetailsInfo.allowPlanCoverEdits;
        updatedInputs.allowPolicyEndDateEdits.value =
          authoritiesDetailsInfo.allowPolicyEndDateEdits;
        updatedInputs.allowClauseEdits.value =
          authoritiesDetailsInfo.allowClauseEdits;
        updatedInputs.allowGroupPolicy.value =
          authoritiesDetailsInfo.allowGroupPolicy;
        updatedInputs.allowEndorsement.value =
          authoritiesDetailsInfo.allowEndorsement;
        updatedInputs.allowEndorsementCostEdits.value =
          authoritiesDetailsInfo.allowEndorsementCostEdits;
        updatedInputs.allowPolicyCorrection.value =
          authoritiesDetailsInfo.allowPolicyCorrection;
      }

      setInputsForm(updatedSections);
    } catch (err) {
      toast.error(<ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>);
    }
  };

  useEffect(() => {
    initialize();
  }, [assignedLinesList]);

  const submitForm = async (values: Record<string, any>) => {
    const [data] = normalizeDynamicFormDrawerValues(drawerSections, values);

    setFormDisabled(true);
    setSubmitButtonState('loading');

    try {
      let variablesMutation = {};
      variablesMutation = {
        entityId: businessUserId,
        allowPlanCoverEdits: data.allowPlanCoverEdits,
        allowPolicyEndDateEdits: data.allowPolicyEndDateEdits,
        allowClauseEdits: data.allowClauseEdits,
        editableLineChargesIDs: data.editableLineCharges || [],
        allowGroupPolicy: data.allowGroupPolicy,
        allowEndorsement: data.allowEndorsement,
        allowEndorsementCostEdits: data.allowEndorsementCostEdits,
        allowPolicyCorrection: data.allowPolicyCorrection,
      };

      authoritiesAction({
        variables: variablesMutation,
        errorPolicy: 'all',
      }).then((res) => {
        if (isEmpty(res.errors)) {
          toast.success(
            <ToastSuccessMessage>
              Authorities of Business User successfully updated.
            </ToastSuccessMessage>
          );
          setTimeout(() => {
            setSubmitButtonState('success');
            onSuccess();
            onClose();
          }, 500);
        } else {
          setSubmitButtonState(undefined);
          toast.error(<ToastErrorMessage>{getError(res)}</ToastErrorMessage>);
        }
      });
    } catch {
      setSubmitButtonState(undefined);
      toast.error(<ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>);
    } finally {
      setFormDisabled(false);
    }
  };

  return (
    <DynamicFormDrawer
      title={'Modify Production Authorities'}
      onClose={() => onClose()}
      isOpen={open}
      loading={assignedLinesLoading}
      sections={inputsForm}
      onSubmit={(values) => submitForm(values)}
      submitButtonState={submitButtonState}
      disableForm={formDisabled}
    />
  );
};

export default SalesForceBusinessUserAuthoritiesDrawer;
